import { ExtendedWarranties } from "./components/ExtendedWarranties/ExtendedWarranties";
import { DeliveryServices } from "./components/DeliveryServices/DeliveryServices";
import { VariantServices } from "../../types/types";
import { useServicesAndWarrantiesContext } from "./ServicesAndWarrantiesContext";
import {
  mappedServices,
  TransformedDeliveryService,
  TransformedExtendedWarranty,
} from "./utils/serviceMapper";
import { useEffect } from "react";
import { HorizontalRule } from "../HorizontalRule/HorizontalRule";
import styles from "./ServicesAndWarranties.module.scss";
import classNames from "classnames/bind";

const cx = classNames.bind(styles);

type Props = {
  /**
   * Indicates if a product variant is available to order.
   */
  availableToOrder: boolean;
  /**
   * The services available for a product variant.
   */
  variantServices: VariantServices;
  /**
   * Feature flag enabling the delivery service questionnaire for complex installs
   */
  deliveryServiceQuestionnaireEnabled: boolean;
  /**
   * Analytics Handler for extended warranties
   */
  extendedWarrantiesAnalyticsHandler: (warrantyTitle: string) => void;
};

export const ServicesAndWarrantiesUI = ({
  availableToOrder,
  variantServices,
  deliveryServiceQuestionnaireEnabled,
  extendedWarrantiesAnalyticsHandler,
}: Props) => {
  const { services, setServices } = useServicesAndWarrantiesContext();

  useEffect(() => {
    setServices(mappedServices(variantServices));
  }, [variantServices]);

  const deliveryServices = services.filter(
    (service) => service.serviceType === "DeliveryService",
  ) as TransformedDeliveryService[];

  const warranties = services.filter(
    (service) => service.serviceType === "ExtendedWarranty",
  ) as TransformedExtendedWarranty[];

  const hasBothServicesAndWarranties =
    deliveryServices.length && warranties.length;
  return (
    <div data-testid="product:services-and-warranties">
      <ExtendedWarranties
        availableToOrder={availableToOrder}
        warranties={warranties}
        extendedWarrantiesAnalyticsHandler={extendedWarrantiesAnalyticsHandler}
      />

      {hasBothServicesAndWarranties ? <HorizontalRule /> : null}

      <div
        className={cx("services", {
          "services--withMargin": hasBothServicesAndWarranties,
        })}
      >
        <DeliveryServices
          availableToOrder={availableToOrder}
          services={deliveryServices}
          deliveryServiceQuestionnaireEnabled={
            deliveryServiceQuestionnaireEnabled
          }
        />
      </div>
    </div>
  );
};
